import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Separator } from 'components/ui/separator';
import { Mail, Linkedin, Github, MapPin, Send } from 'lucide-react';
import { useLanguage } from 'contexts/LanguageContext';

const Contact: React.FC = () => {
  const { t } = useLanguage();
  const contactMethods = [
    {
      icon: Mail,
      title: t.contactSection.email,
      value: 'zakariadani25@gmail.com',
      link: 'mailto:zakariadani25@gmail.com',
      color: 'text-blue-500',
    },
    {
      icon: Linkedin,
      title: 'LinkedIn',
      value: t.contactSection.linkedinValue,
      link: 'https://www.linkedin.com/in/zakaria-dani-5a2961121/',
      color: 'text-blue-600',
    },
    {
      icon: Github,
      title: 'GitHub',
      value: '@ZakariaDani',
      link: 'https://github.com/ZakariaDani',
      color: 'text-gray-700 dark:text-gray-300',
    },
    {
      icon: MapPin,
      title: t.contactSection.location,
      value: t.contactSection.remote,
      link: null,
      color: 'text-green-500',
    },
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            {t.contactSection.badge}
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            {t.contactSection.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.contactSection.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Information Card */}
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="text-2xl">{t.contactSection.infoTitle}</CardTitle>
              <CardDescription>
                {t.contactSection.infoDescription}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {contactMethods.map((method, index) => (
                <div key={method.title}>
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg bg-muted flex items-center justify-center ${method.color}`}>
                      <method.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm text-muted-foreground mb-1">
                        {method.title}
                      </h3>
                      {method.link ? (
                        <a
                          href={method.link}
                          target={method.link.startsWith('http') ? '_blank' : undefined}
                          rel={method.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-foreground hover:text-primary transition-colors font-medium"
                        >
                          {method.value}
                        </a>
                      ) : (
                        <p className="text-foreground font-medium">{method.value}</p>
                      )}
                    </div>
                  </div>
                  {index < contactMethods.length - 1 && <Separator className="mt-6" />}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Quick Message Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">{t.contactSection.messageTitle}</CardTitle>
              <CardDescription>
                {t.contactSection.messageDescription}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-3">{t.contactSection.helpTitle}</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {t.contactSection.helpItems.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Separator />

                <div>
                  <h3 className="font-semibold mb-3">{t.contactSection.responseTitle}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t.contactSection.responseText}
                  </p>
                </div>

                <div className="pt-4 space-y-3">
                  <Button size="lg" className="w-full" asChild>
                    <a href="mailto:zakariadani25@gmail.com">
                      <Send className="mr-2 h-4 w-4" />
                      {t.contactSection.sendEmail}
                    </a>
                  </Button>
                  <Button size="lg" variant="outline" className="w-full" asChild>
                    <a
                      href="https://www.linkedin.com/in/zakaria-dani-5a2961121/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Linkedin className="mr-2 h-4 w-4" />
                      {t.contactSection.connectLinkedin}
                    </a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional CTA */}
        <Card className="mt-8 bg-primary/5 border-primary/20">
          <CardContent className="py-8">
            <div className="text-center">
              <h3 className="text-xl font-bold mb-2">{t.contactSection.ctaTitle}</h3>
              <p className="text-muted-foreground mb-6">
                {t.contactSection.ctaText}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="outline" asChild>
                  <a href="/resume.pdf" download>
                    {t.contactSection.downloadResume}
                  </a>
                </Button>
                <Button asChild>
                  <a href="https://github.com/ZakariaDani" target="_blank" rel="noopener noreferrer">
                    <Github className="mr-2 h-4 w-4" />
                    {t.contactSection.viewGithub}
                  </a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default Contact;

